import { useState, type KeyboardEvent, type ReactNode } from 'react';
import type { ObjectId, SpatialObject } from '@/types';
import { useSelectionStore } from '@/store/selectionStore';
import { useWorkspaceStore, selectActiveDocument } from '@/store/workspaceStore';
import { useObject } from '@/hooks/useObject';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { objectType } from '@/core/scene/objectTypes';
import { degToRad, radToDeg } from '@/core/geometry/transform';
import {
  renameObjectCommand,
  setLockedCommand,
  setTransformCommand,
  setVisibilityCommand,
  setZIndexCommand,
} from '@/features/objects/objectCommands';
import { ConnectionInspector } from './ConnectionInspector';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import InputBase from '@mui/material/InputBase';
import Tooltip from '@mui/material/Tooltip';
import styles from './Inspector.module.css';

/**
 * Metadata and transform editor for the selection. Single selection shows the full
 * panel; multi-selection shows a summary with shared toggles. Every edit is a command,
 * so it is undoable, and inputs commit on blur/Enter rather than per keystroke.
 */
export const Inspector = () => {
  const ids = useSelectionStore((s) => s.selection.objectIds);
  const connectionIds = useSelectionStore((s) => s.selection.connectionIds);
  const breakpoint = useBreakpoint();
  if (ids.length === 0 && connectionIds.length === 0) return null;
  return (
    <Paper
      component="aside"
      className={breakpoint === 'compact' ? styles.sheet : styles.panel}
      aria-label="Inspector"
      data-testid="inspector"
      onPointerDown={(e) => {
        e.stopPropagation();
      }}
    >
      {ids.length === 0 && connectionIds[0] ? (
        <ConnectionInspector id={connectionIds[0]} />
      ) : (
        <InspectorContent ids={ids} />
      )}
    </Paper>
  );
};

/** Panel-agnostic inspector body; shared by the floating panel and the Inspector application. */
export const InspectorContent = ({ ids }: { ids: readonly ObjectId[] }) =>
  ids.length === 1 && ids[0] ? <SingleInspector id={ids[0]} /> : <MultiInspector ids={ids} />;

// ---------------------------------------------------------------------------

const useDispatch = () => {
  const dispatch = useWorkspaceStore((s) => s.dispatch);
  return (make: (doc: NonNullable<ReturnType<typeof selectActiveDocument>>) => ReturnType<typeof setZIndexCommand>) => {
    const doc = selectActiveDocument(useWorkspaceStore.getState());
    if (!doc) return;
    const cmd = make(doc);
    if (cmd) dispatch(cmd);
  };
};

const SingleInspector = ({ id }: { id: ObjectId }) => {
  const obj = useObject(id);
  const parent = useWorkspaceStore((s) => {
    const doc = s.workspaces[s.activeWorkspaceId];
    const p = obj?.parentId;
    return p && doc ? doc.objects[p] : undefined;
  });
  const run = useDispatch();
  if (!obj) return null;
  const def = objectType(obj.kind);
  const t = obj.transform;
  const disabled = obj.locked;

  return (
    <>
      <header className={styles.header}>
        <span className={styles.kind}>{def.label}</span>
        <TextField
          value={obj.name}
          ariaLabel="Name"
          testId="inspector-name"
          onCommit={(name) => {
            run((doc) => renameObjectCommand(doc, id, name));
          }}
        />
      </header>

      <Section title="Position">
        <NumberField
          label="X"
          value={t.position.x}
          disabled={disabled}
          testId="inspector-x"
          onCommit={(x) => {
            run((doc) => setTransformCommand(doc, id, { position: { x, y: t.position.y } }, 'Move object'));
          }}
        />
        <NumberField
          label="Y"
          value={t.position.y}
          disabled={disabled}
          testId="inspector-y"
          onCommit={(y) => {
            run((doc) => setTransformCommand(doc, id, { position: { x: t.position.x, y } }, 'Move object'));
          }}
        />
      </Section>

      <Section title="Size">
        <NumberField
          label="W"
          value={obj.size.width}
          min={def.minSize.width}
          disabled={disabled || !def.resizable}
          testId="inspector-w"
          onCommit={(width) => {
            run((doc) => setTransformCommand(doc, id, { size: { width, height: obj.size.height } }, 'Resize object'));
          }}
        />
        <NumberField
          label="H"
          value={obj.size.height}
          min={def.minSize.height}
          disabled={disabled || !def.resizable}
          testId="inspector-h"
          onCommit={(height) => {
            run((doc) => setTransformCommand(doc, id, { size: { width: obj.size.width, height } }, 'Resize object'));
          }}
        />
      </Section>

      <Section title="Transform">
        <NumberField
          label="Rotation"
          unit="°"
          value={radToDeg(t.rotation)}
          step={1}
          disabled={disabled || !def.rotatable}
          testId="inspector-rotation"
          onCommit={(deg) => {
            run((doc) => setTransformCommand(doc, id, { rotation: degToRad(deg) }, 'Rotate object'));
          }}
        />
        <NumberField
          label="Scale X"
          value={t.scale.x}
          step={0.05}
          min={0.05}
          disabled={disabled}
          testId="inspector-scale-x"
          onCommit={(x) => {
            run((doc) => setTransformCommand(doc, id, { scale: { x, y: t.scale.y } }, 'Scale object'));
          }}
        />
        <NumberField
          label="Scale Y"
          value={t.scale.y}
          step={0.05}
          min={0.05}
          disabled={disabled}
          testId="inspector-scale-y"
          onCommit={(y) => {
            run((doc) => setTransformCommand(doc, id, { scale: { x: t.scale.x, y } }, 'Scale object'));
          }}
        />
        <NumberField
          label="Z-index"
          value={obj.zIndex}
          step={1}
          disabled={disabled}
          testId="inspector-z"
          onCommit={(z) => {
            run((doc) => setZIndexCommand(doc, id, z));
          }}
        />
      </Section>

      <Section title="State">
        <Toggle
          label="Visible"
          checked={obj.visible}
          testId="inspector-visible"
          onChange={(v) => {
            run((doc) => setVisibilityCommand(doc, [id], v));
          }}
        />
        <Toggle
          label="Locked"
          checked={obj.locked}
          testId="inspector-locked"
          onChange={(v) => {
            run((doc) => setLockedCommand(doc, [id], v));
          }}
        />
      </Section>

      <Section title="Identity">
        <Row label="Type">
          <span className={styles.mono}>{obj.kind}</span>
        </Row>
        <Row label="ID">
          <IdChip id={obj.id} />
        </Row>
        <Row label="Parent">
          <span className={styles.mono}>{parent ? parent.name : '— (root)'}</span>
        </Row>
        <Row label="Updated">
          <span className={styles.mono}>{new Date(obj.metadata.updatedAt).toLocaleTimeString()}</span>
        </Row>
      </Section>
    </>
  );
};

const MultiInspector = ({ ids }: { ids: readonly ObjectId[] }) => {
  // Selectors return primitives so the store's identity check never sees a fresh array.
  const summary = useWorkspaceStore((s) => {
    const doc = s.workspaces[s.activeWorkspaceId];
    const objects = doc ? ids.map((id) => doc.objects[id]).filter((o): o is SpatialObject => !!o) : [];
    const kinds = [...new Set(objects.map((o) => o.kind))].sort().join(', ');
    return `${objects.length}|${kinds}|${objects.every((o) => o.visible) ? 1 : 0}|${objects.every((o) => o.locked) ? 1 : 0}`;
  });
  const run = useDispatch();
  const [countText, kinds, visibleFlag, lockedFlag] = summary.split('|');
  const count = Number(countText);
  const allVisible = visibleFlag === '1';
  const allLocked = lockedFlag === '1';
  return (
    <>
      <header className={styles.header}>
        <span className={styles.kind}>Selection</span>
        <strong data-testid="inspector-count">{count} objects</strong>
      </header>
      <Section title="Contents">
        <Row label="Types">
          <span className={styles.mono}>{kinds}</span>
        </Row>
      </Section>
      <Section title="State">
        <Toggle
          label="Visible"
          checked={allVisible}
          onChange={(v) => {
            run((doc) => setVisibilityCommand(doc, ids, v));
          }}
        />
        <Toggle
          label="Locked"
          checked={allLocked}
          onChange={(v) => {
            run((doc) => setLockedCommand(doc, ids, v));
          }}
        />
      </Section>
    </>
  );
};

// ---------------------------------------------------------------------------
// Primitives

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className={styles.section}>
    <h3 className={styles.sectionTitle}>{title}</h3>
    <div className={styles.grid}>{children}</div>
  </section>
);

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <label className={styles.row}>
    <span className={styles.label}>{label}</span>
    <span className={styles.value}>{children}</span>
  </label>
);

const round = (n: number, digits = 2) => Math.round(n * 10 ** digits) / 10 ** digits;

interface NumberFieldProps {
  readonly label: string;
  readonly value: number;
  readonly onCommit: (value: number) => void;
  readonly step?: number;
  readonly min?: number;
  readonly unit?: string;
  readonly disabled?: boolean;
  readonly testId?: string;
}

const NumberField = ({ label, value, onCommit, step = 1, min, unit, disabled, testId }: NumberFieldProps) => {
  const [text, setText] = useState(() => String(round(value)));
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    // External change (drag, undo): resync the draft without an effect.
    setSeen(value);
    setText(String(round(value)));
  }
  const commit = () => {
    const n = Number(text);
    if (!Number.isFinite(n)) {
      setText(String(round(value)));
      return;
    }
    const clamped = min !== undefined ? Math.max(min, n) : n;
    if (clamped !== round(value)) onCommit(clamped);
    else setText(String(round(value)));
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
    if (e.key === 'Escape') {
      setText(String(round(value)));
      e.currentTarget.blur();
    }
    e.stopPropagation();
  };
  return (
    <Row label={label}>
      <InputBase
        className={styles.field}
        type="number"
        value={text}
        disabled={disabled}
        inputProps={{ inputMode: 'decimal', step, 'aria-label': label, 'data-testid': testId }}
        onChange={(e) => {
          setText(e.target.value);
        }}
        onBlur={commit}
        onKeyDown={onKeyDown}
      />
      {unit && <span className={styles.unit}>{unit}</span>}
    </Row>
  );
};

const TextField = ({
  value,
  onCommit,
  ariaLabel,
  testId,
}: {
  value: string;
  onCommit: (v: string) => void;
  ariaLabel: string;
  testId?: string;
}) => {
  const [text, setText] = useState(value);
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setText(value);
  }
  return (
    <input
      className={styles.title}
      value={text}
      aria-label={ariaLabel}
      data-testid={testId}
      onChange={(e) => {
        setText(e.target.value);
      }}
      onBlur={() => {
        if (text.trim() && text !== value) onCommit(text);
        else setText(value);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        e.stopPropagation();
      }}
    />
  );
};

const Toggle = ({
  label,
  checked,
  onChange,
  testId,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  testId?: string;
}) => (
  <Row label={label}>
    <Checkbox
      checked={checked}
      slotProps={{ input: { 'aria-label': label, 'data-testid': testId } as Record<string, unknown> }}
      onChange={(e) => {
        onChange(e.target.checked);
      }}
    />
  </Row>
);

const IdChip = ({ id }: { id: string }) => {
  const short = id.length > 14 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id;
  return (
    <Tooltip title={`${id} — click to copy`}>
      <Chip
        label={short}
        variant="outlined"
        clickable
        className={styles.idChip}
        aria-label={`Copy id ${id}`}
        onClick={() => {
          if ('clipboard' in navigator) void navigator.clipboard.writeText(id);
        }}
      />
    </Tooltip>
  );
};
